import { Promise as BluebirdPromise } from 'bluebird';
import cns from 'classnames';
import { get, isEmpty, isEqual, keyBy } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, withRouter } from 'react-router-dom';
import ReferenceGuidePDF from '../../../client/containers/ReferenceGuidePDF';
import Page from '../../components/Page';
import RoutingTabs from '../../components/RoutingTabs';
import VerbProgressBar from '../../components/VerbProgressBar';
import { LEARNING_ASSET_TYPES, SEGMENT_EVENTS } from '../../Constants';
import { isValidId } from '../../utils/StringUtils';
import CollectionJourneySection from '../Collection/CollectionJourneySection';
import MobileCollectionJourneySection from '../Collection/MobileCollectionJourneySection';
import CollectionLearningAssetRow from '../Collection/CollectionLearningAssetRow';
import UserCollectionLearningAssetRow from './UserCollectionLearningAssetRow';
import MobileUserCollectionLearningAssetRow from './MobileUserCollectionLearningAssetRow';
import { RuntimeContext } from '../../../../common/RuntimeContext';
import MyCollectionTrackAchievement from '../../../client/containers/MyCollection/MyCollectionTrackAchievement';
import MyCollectionTrackDiscussionTab from '../../../client/containers/MyCollection/MyCollectionTrackDiscussionTab';
import {
    clearSearchedProfiles,
    getBadge,
    getCollection,
    getCollectionProfiles,
    getManyLearningAssets,
    getManyPeerRecommendations,
    getManyTeams,
    getManyUserLearningAssets,
    hideModal,
    showModal,
    getManyUserCollections,
    postUserCollection
} from '../../actions';
import VerbHeading from '../../components/VerbHeading';
import { makeGetCollectionContent, makeGetUserCollectionContent } from '../../selectors';
import { getCompletionStatusForUserCollection } from '../../utils/UserLearningAssetUtils';
import CollectionBanner from '../Collection/CollectionBanner';
import MobileCollectionBanner from '../Collection/MobileCollectionBanner';

class MyCollection extends Component {
    static propTypes = {
        userCollection: PropTypes.object,
        userCollectionAssets: PropTypes.array,
        loggedInUser: PropTypes.object,
        getManyUserLearningAssets: PropTypes.func,
        getManyTeams: PropTypes.func,
        getManyPeerRecommendations: PropTypes.func,
        clearSearchedProfiles: PropTypes.func,
        history: PropTypes.object,
        match: PropTypes.object,
        getBadge: PropTypes.func,
        behaviorsById: PropTypes.object,
        badgesById: PropTypes.object,
        getCollection: PropTypes.func,
        getManyLearningAssets: PropTypes.func,
        collectionContent: PropTypes.array,
        loggedInUserCompany: PropTypes.object,
        collectionProfiles: PropTypes.array,
        isVerbAdmin: PropTypes.bool,
        isTeamAdmin: PropTypes.bool,
        isAccountOwner: PropTypes.bool,
        collection: PropTypes.object,
        teamsById: PropTypes.object,
        location: PropTypes.object,
        learningAssetsById: PropTypes.object,
        hideModal: PropTypes.func,
        showModal: PropTypes.func,
        referenceSectionItems: PropTypes.array,
        hasReferences: PropTypes.bool
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);

        const initialState = {
            showBanner: false,
            isLoading: true,
            recommender: '',
            currentRecommendation: {},
            screenWidth: window.innerWidth,
        };

        const showBanner = get(props, 'location.state.showBanner', false);

        if (showBanner) {
            initialState.showBanner = true;
        }

        this.state = initialState;
    }

    updateScreenWidth = () => {
      this.setState({screenWidth: window.innerWidth});
    }

    componentDidMount() {
        const { clearSearchedProfiles, match, userCollection, loggedInUser = {} } = this.props;

        clearSearchedProfiles();

        window.addEventListener('resize', this.updateScreenWidth);
        return this._getAllCollectionData();
    }

    componentDidUpdate(prevProps) {
        const { userCollection, match, loggedInUser } = this.props;
        if (!isEqual(prevProps.userCollection, userCollection)) {
            return this._getAllCollectionData();
        }
        return null;
    }

    componentWillUnmount() {
      window.removeEventListener('resize', this.updateScreenWidth);
    }

    _getLatestRecommendation = () => {
        const { getManyPeerRecommendations, loggedInUser, teamsById, match } = this.props;

        return getManyPeerRecommendations({
            recommendedTo: loggedInUser._id,
            isRecommendedByTeamLead: true,
            parentId: match.params.id,
            parentType: 'COLLECTION',
            pathsContext: this.context.pathsWeb
        }).then(({ collectionRecommendations = [] }) => {
            let latestRecommendation = collectionRecommendations[0] || {};
            if (latestRecommendation.teamId) {
                const team = teamsById[latestRecommendation.teamId] || {};
                for (let i = 0; i < (team.recommendedCollections || []).length; i += 1) {
                    const { collectionId, dueDate, assigned, message = '' } = team.recommendedCollections[i] || {};
                    if (collectionId === latestRecommendation.parentId) {
                        latestRecommendation = {
                            ...latestRecommendation,
                            note: message.length ? message : latestRecommendation.note,
                            dueDate,
                            assigned,
                            teamName: team.name
                        };
                        break;
                    }
                }
            }

            return latestRecommendation;
        });
    };

    _getAllCollectionData = () => {
        const {
            getManyUserLearningAssets,
            loggedInUser,
            getBadge,
            getCollection,
            getManyLearningAssets,
            getManyTeams,
            userCollection
        } = this.props;
        const userLearningAssetIds = [];
        (userCollection.content || []).forEach((item) => {
            if (item.userLearningAssetId) {
                userLearningAssetIds.push(item.userLearningAssetId);
            }
        });
        const learningAssetIds = (userCollection.content || []).map((item) => item.learningAssetId);

        const promises = [
            this._getLatestRecommendation(),
            getManyLearningAssets({
                _id: learningAssetIds,
                getFullProjection: true,
                pathsContext: this.context.pathsWeb
            }),
            getManyUserLearningAssets({
                userProfileId: loggedInUser._id,
                fullprojection: true,
                pathsContext: this.context.pathsWeb
            }),
            getManyTeams({ pathsContext: this.context.pathsWeb }),
            getCollection({ collectionId: userCollection.collectionId, pathsContext: this.context.pathsWeb }).reflect()
        ];

        if (userCollection.badgeId) {
            promises.push(getBadge({ id: userCollection.badgeId, pathsContext: this.context.pathsWeb }));
        }
        return BluebirdPromise.all(promises).then(([latestRecommendation]) => {
            this.setState({
                isLoading: false,
                latestRecommendation
            });
        });
    };

    createUserCollection = (values) => {
        const { collection, postUserCollection, loggedInUser, getManyUserCollections } = this.props;
        const newUserCollection = { ...values };
        newUserCollection.collectionId = collection._id;
        const params = {
            collectionId: collection._id,
            userProfileId: loggedInUser._id
        };

        return getManyUserCollections({ ...params, pathsContext: this.context.pathsWeb }).then((collections) => {
            if (!collections.length) {
                return postUserCollection({ body: newUserCollection, pathsContext: this.context.pathsWeb });
            }
            return collections[0];
        });
    };

    _displayUserCollectionBanner = () => {
        const {
            userCollection,
            teamsById,
            collection,
            collectionContent,
            collectionProfiles,
            loggedInUser: { _id, teams = [] },
            showModal,
            hideModal,
            referenceSectionItems
        } = this.props;
        let isTeamLead = false;
        teams.forEach((team) => {
            if (teamsById[team.teamId] && teamsById[team.teamId].teamLeads.indexOf(_id) > -1) {
                isTeamLead = true;
            }
        });

        if (this.state.screenWidth <= 480) {
          return (
            <MobileCollectionBanner
                collection={collection}
                userCollection={userCollection}
                className={'user-collections-widget'}
                createUserCollection={this.createUserCollection}
                memberProfiles={collectionProfiles}
                collectionContent={collectionContent}
                showModal={showModal}
                hideModal={hideModal}
                referenceSectionItems={referenceSectionItems}
            />
          );
        }

        return (
            <CollectionBanner
                collection={collection}
                userCollection={userCollection}
                className={'user-collections-widget'}
                createUserCollection={this.createUserCollection}
                memberProfiles={collectionProfiles}
                collectionContent={collectionContent}
                showModal={showModal}
                hideModal={hideModal}
                referenceSectionItems={referenceSectionItems}
            />
        );
    };

    _displayDiscoverWelcomeBanner = () => {
        const {
            userCollection: { collectionName, name }
        } = this.props;
        const nameToRender = <span className={'collection-name'}>{name || collectionName}</span>;
        return (
            <div className={'welcome-container'}>
                <div className={'row'}>
                    <VerbHeading
                        hNumber={4}
                        className={'congrats-banner'}
                        messageId={'COLLECTION_STARTING_CONGRATS'}
                        defaultMessage={
                            "Congratulations! You're about to start working on {CollectionName}! Review your new collection below and then click BEGIN or click any of the learning items to get started."
                        }
                        valuesToInject={{ CollectionName: nameToRender }}
                    />
                </div>
            </div>
        );
    };

    goToUserLearningAsset = (userLearningAssetId) => {
        const { history, userCollection } = this.props;
        return history.push(`/workspace/${userLearningAssetId}`, {
            userCollection: {
                id: userCollection._id,
                started: true
            }
        });
    };

    _orderActivities = (userCollectionAssetsByTrackId = {}) => {
        const {
            collection: { contentIds = [] },
            learningAssetsById
        } = this.props;
        const orderedCollectionAssets = [];
        contentIds.forEach(({ id }) => {
            if (userCollectionAssetsByTrackId[id]) {
                orderedCollectionAssets.push(userCollectionAssetsByTrackId[id]);
            } else {
                orderedCollectionAssets.push({ ...learningAssetsById[id], isLA: true });
            }
        });

        return orderedCollectionAssets;
    };

    handleAssetRowClick = (learningAsset) => {
        const { history } = this.props;
        history.push(`/assetLink/${learningAsset._id}`);
    };

    _getCollectionLearningAssetRow = ({
        index,
        asset,
        userCollectionAssetsByTrackId,
        userCollectionContentByAssetId,
        isLockedReflection = false,
        hideDivider = false,
        isReflection = false,
        status = ''
    }) => {
        let lockChain = true;
        const { history, userCollectionAssets, collection, userCollection } = this.props;
        let assetIsLocked = true;
        if (index === 0) {
            assetIsLocked = false;
        } else if (userCollectionAssets[index - 1]?.isCompleted && lockChain) {
            assetIsLocked = false;
        } else {
            assetIsLocked =
                !!userCollectionContentByAssetId[asset._id] && userCollectionContentByAssetId[asset._id].isLocked;
            lockChain = false;
        }

        if (asset.isLA) {
            return (
                <CollectionLearningAssetRow
                    key={asset._id}
                    isLocked={assetIsLocked || isLockedReflection}
                    isLockedReflection={isLockedReflection}
                    history={history}
                    handleAssetRowClick={this.handleAssetRowClick}
                    collection={collection}
                    userLearningAssetsByAssetId={userCollectionAssetsByTrackId}
                    learningAsset={asset}
                />
            );
        }

        if (this.state.screenWidth <= 480) {
          return (
            <MobileUserCollectionLearningAssetRow
                key={asset._id}
                isLocked={assetIsLocked || isLockedReflection}
                isLockedReflection={isLockedReflection}
                goToUserLearningAsset={this.goToUserLearningAsset}
                userLearningAsset={asset}
                userCollection={userCollection}
                hideDivider={hideDivider}
                isReflection={isReflection}
                status={status}
            />
          );
        }
        return (
            <UserCollectionLearningAssetRow
                key={asset._id}
                isLocked={assetIsLocked || isLockedReflection}
                isLockedReflection={isLockedReflection}
                goToUserLearningAsset={this.goToUserLearningAsset}
                userLearningAsset={asset}
                userCollection={userCollection}
                hideDivider={hideDivider}
                isReflection={isReflection}
                status={status}
            />
        );
    };

    _renderLearningJourneyTab = () => {
        const { userCollectionAssets, userCollection, collectionContent = [] } = this.props;
        const { isLoading, latestRecommendation } = this.state;
        const validTrackIds = new Set(collectionContent.map(collection => collection._id))
        const userCollectionContentByAssetId = keyBy(userCollection.content, 'userLearningAssetId');
        const userCollectionAssetsByTrackId = keyBy(userCollectionAssets, 'trackId');

        const orderedUserCollectionAssets = userCollectionAssets.length
            ? this._orderActivities(userCollectionAssetsByTrackId)
            : userCollectionAssets;

        const isPracticalApplication = orderedUserCollectionAssets.some((asset) =>
            [LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION, LEARNING_ASSET_TYPES.REFLECTION].includes(asset.type)
        );

        const updatedOrderedUserCollectionAssets = orderedUserCollectionAssets.filter(asset => validTrackIds.has(asset.trackId))

        if (this.state.screenWidth <= 480) {
          return (
            <>
                <div className={'learning-journey-tab asset-row-wrap'}>
                    {
                        <MobileCollectionJourneySection
                            {...this.props}
                            learningAssets={updatedOrderedUserCollectionAssets}
                            isPracticalApplication={isPracticalApplication}
                            rowRenderer={(asset, i, isLockedReflection, hideDivider, isReflection, status) =>
                                this._getCollectionLearningAssetRow({
                                    index: i,
                                    asset,
                                    userCollectionAssetsByTrackId,
                                    userCollectionContentByAssetId,
                                    isLockedReflection,
                                    hideDivider,
                                    isReflection,
                                    status
                                })
                            }
                        />
                    }
                    {this._renderAchievementRow()}
                </div>
            </>
        );
        }

        return (
            <>
                <div className={'learning-journey-tab asset-row-wrap'}>
                    {
                        <CollectionJourneySection
                            {...this.props}
                            learningAssets={updatedOrderedUserCollectionAssets}
                            isPracticalApplication={isPracticalApplication}
                            rowRenderer={(asset, i, isLockedReflection, hideDivider, isReflection, status) =>
                                this._getCollectionLearningAssetRow({
                                    index: i,
                                    asset,
                                    userCollectionAssetsByTrackId,
                                    userCollectionContentByAssetId,
                                    isLockedReflection,
                                    hideDivider,
                                    isReflection,
                                    status
                                })
                            }
                        />
                    }
                    {this._renderAchievementRow()}
                </div>
            </>
        );
    };

    _renderTrackDiscussionTab = () => {
        const {
            userCollection,
            loggedInUserCompany,
            loggedInUser,
            isVerbAdmin,
            isAccountOwner,
            isTeamAdmin,
            collection,
            learningAssetsById,
            hideModal,
            showModal,
            referenceSectionItems
        } = this.props;
        return (
            <MyCollectionTrackDiscussionTab
                learningAssetsById={learningAssetsById}
                userCollection={userCollection}
                loggedInUserCompany={loggedInUserCompany}
                loggedInUser={loggedInUser}
                isVerbAdmin={isVerbAdmin}
                isAccountOwner={isAccountOwner}
                isTeamAdmin={isTeamAdmin}
                collection={collection}
                showModal={showModal}
                hideModal={hideModal}
                referenceSectionItems={referenceSectionItems}
            />
        );
    };

    _renderReferenceTab = () => {
        const {
            userCollection: { collectionId = '' }
        } = this.props;
        return <ReferenceGuidePDF collectionId={collectionId} />;
    };

    _renderAchievementRow = () => {
        const {
            userCollectionAssets,
            userCollection,
            behaviorsById,
            badgesById,
            loggedInUserCompany,
            loggedInUser,
            isVerbAdmin,
            collection
        } = this.props;
        return (
            <MyCollectionTrackAchievement
                badgesById={badgesById}
                behaviorsById={behaviorsById}
                userCollectionAssets={userCollectionAssets}
                userCollection={userCollection}
                loggedInUser={loggedInUser}
                loggedInUserCompany={loggedInUserCompany}
                isVerbAdmin={isVerbAdmin}
                collection={collection}
            />
        );
    };

    _makeAvailableRoutesForTabs = () => {
        const { loggedInUserCompany, match, userCollection, isVerbAdmin, hasReferences = false } = this.props;
        const tabList = [
            {
                tabName: 'Path',
                path: `/collection/${match.params.id}/path`,
                exact: true,
                render: this._renderLearningJourneyTab
            }
        ];

        if (hasReferences) {
            tabList.push({
                tabName: 'Summary',
                path: `/collection/${match.params.id}/summary`,
                exact: true,
                render: this._renderReferenceTab,
                onClick: () => {}
            });
        }

        tabList.push({
            tabName: 'Discussion Guide',
            path: `/collection/${match.params.id}/discussion`,
            exact: true,
            render: this._renderTrackDiscussionTab,
            onClick: () => {}
        });

        return tabList;
    };

    getCollectionProfilesData = (params) => {
        const { userCollection } = this.props;
        const newParams = {
            collectionId: userCollection.collectionId,
            ignoreDispatch: true,
            pathsContext: this.context.pathsWeb,
            ...params
        };
        return getCollectionProfiles(newParams);
    };

    render() {
        const { showBanner } = this.state;
        const {
            userCollection,
            match: {
                params: { section }
            }
        } = this.props;
        if (!section) {
            return <Redirect to={`/collection/${this.props.match.params.id}/path`} />;
        }

        const availableRoutes = this._makeAvailableRoutesForTabs();
        const routes = availableRoutes.map((routeItem) => (
            <Route key={routeItem.path} path={routeItem.path} exact={routeItem.exact} render={routeItem.render} />
        ));

        const showProgressBar = userCollection?.content?.some((asset) => asset.isCompleted) || this.state.screenWidth <= 480;

        return (
            <Page className={this.state.screenWidth <= 480 ? 'mobile-collection-banner' : 'my-collection-v3'}>
                {this._displayUserCollectionBanner()}
                {showBanner && this._displayDiscoverWelcomeBanner()}
                {
                    showProgressBar && 
                    <div className={'collection-progress'}>
                        <VerbProgressBar
                            totalSteps={userCollection?.content?.length ?? 0}
                            currentStep={userCollection.content.filter((asset) => asset.isCompleted).length}
                        />
                    </div>
                }
                <div className={'collection-page-tab-header'}>
                    <RoutingTabs routeTabs={availableRoutes} tabAlignment={'mx-auto'} />
                </div>
                <div className={cns('tab-container', { container: section !== 'trackgrowth' })}>{routes}</div>
            </Page>
        );
    }
}

const makeMapStateToProps = () => {
    const getUserCollectionContent = makeGetUserCollectionContent();
    const getCollectionContent = makeGetCollectionContent();

    const derivedMapStateToProps = (state, ownProps) => {
        const {
            auth: { loggedInUser, loggedInUserCompanyId, isVerbAdmin, isTeamAdmin, isAccountOwner },
            userLearningAssets: { loading },
            companies,
            behaviors,
            badges,
            collections,
            learningAssets: { byId: learningAssetsById },
            teams: { byId: teamsById = {} }
        } = state;

        // User Collection
        const userCollection = get(ownProps, 'userCollection', {});
        const collection = collections.byId[userCollection && userCollection.collectionId];

        // User Learning Assets in User Collection
        const userCollectionAssets = getUserCollectionContent(userCollection, state);
        const userCollectionWithCompletionStatus = {
            ...userCollection,
            ...getCompletionStatusForUserCollection(userCollectionAssets)
        };

        const collectionContent = getCollectionContent(collection, state);
        const loggedInUserCompany = (companies.byId && companies.byId[loggedInUserCompanyId]) || {};
        let referenceSectionItems = [];
        if (collectionContent.length) {
            referenceSectionItems = collectionContent
                .map((la) => la.sectionItems)
                .flat(1)
                .filter((si) => si && si.isIncludedInDiscussionGuide);
        }
        const hasReferences = !!referenceSectionItems.length;
        return {
            userCollection: userCollectionWithCompletionStatus,
            userCollectionAssets,
            loggedInUser,
            userLearningAssetLoading: loading,
            behaviorsById: behaviors.byId,
            badgesById: badges.byId,
            loggedInUserCompany,
            isVerbAdmin,
            isTeamAdmin,
            isAccountOwner,
            collection,
            collectionContent,
            teamsById,
            learningAssetsById,
            referenceSectionItems,
            hasReferences
        };
    };

    return derivedMapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
    getManyUserLearningAssets: (params) => dispatch(getManyUserLearningAssets(params)),
    clearSearchedProfiles: (params) => dispatch(clearSearchedProfiles(params)),
    getBadge: (params) => dispatch(getBadge(params)),
    getCollection: (params) => dispatch(getCollection(params)),
    getManyLearningAssets: (params) => dispatch(getManyLearningAssets(params)),
    getManyTeams: (params) => dispatch(getManyTeams(params)),
    showModal: (params) => dispatch(showModal(params)),
    hideModal: (params) => dispatch(hideModal(params)),
    getManyPeerRecommendations: (params) => dispatch(getManyPeerRecommendations(params)),
    getManyUserCollections: (params) => dispatch(getManyUserCollections(params)),
    postUserCollection: (params) => dispatch(postUserCollection(params)),

});

export const MyCollectionComponent = MyCollection;
export default withRouter(connect(makeMapStateToProps, mapDispatchToProps)(MyCollection));
